/**
 * The theme's lead forms, posted through MV Qore's SDK.
 *
 * The SDK owns the transport: attachLeadForm() takes the form over on submit,
 * injects its own honeypot, stamps render time, attaches the active referrer
 * and posts. This file owns everything a shopper sees — the consent gating, the
 * captcha mount and the success state. The referrer step is shared with the Pro
 * application and lives in pruvit-referrer-step.js.
 *
 * Two things the SDK will not do for us, both of which fail closed and quietly:
 *
 *   1. The captcha. Bot protection is enabled on this store, so a submission
 *      with no frc-captcha-response is rejected server-side. The widget used to
 *      arrive inside MV Qore's block bundle; that bundle is block-scoped, so
 *      removing their block removed the widget. We mount our own.
 *
 *   2. A lead cannot be submitted within 5 seconds of the form rendering — the
 *      server treats faster than that as scripted. Correct for humans, and
 *      surprising while testing: an immediate submit fails with LEAD_TOO_FAST.
 */
(() => {
  const roots = document.querySelectorAll('[data-pruvit-lead]');
  if (!roots.length) return;

  /*
   * Friendly Captcha v2, and the version is the whole point.
   *
   * This loaded `friendly-challenge@0.9.18` — the v1 package — while MV Qore
   * verify the answer against Friendly Captcha's v2 siteverify API. A v1
   * solution is not a v2 response, so every Create Account submission came
   * back `response_invalid`. Their dev team read it out of the Friendly
   * Captcha logs on 2026-09-23 and were right about the cause.
   *
   * The two are told apart by the field the widget writes:
   *   v1  frc-captcha-solution   9ce0...092.arOmGfD3nM+AFhk1AQwwmw...
   *   v2  frc-captcha-response   AQQA.GBFmgsmgjUX4dZuWWGJ...
   *
   * v2 names it `frc-captcha-response`, which is exactly the field
   * MVQore.attachLeadForm reads — so the widget now fills it directly and the
   * theme no longer carries a hidden input of its own to copy into. Two inputs
   * of that name in one form is the bug this would otherwise become.
   *
   * The sitekey did not have to change: FCMV1TSSPU01C69L mounts and solves
   * under v2, verified on staging before this was written.
   */
  const FRIENDLY_CAPTCHA_SRC = 'https://cdn.jsdelivr.net/npm/@friendlycaptcha/sdk@1.1.1/site.min.js';

  const strings = (() => {
    const tag = document.querySelector('[data-pruvit-lead-strings]');
    try {
      return tag ? JSON.parse(tag.textContent) : {};
    } catch (e) {
      return {};
    }
  })();

  const say = (key, fallback) => strings[key] || fallback;

  /**
   * The `t` filter HTML-escapes what it interpolates, and these strings are
   * read back out of a JSON script tag and written with textContent — which
   * does not decode entities. Without this a name with an apostrophe arrives
   * as isn&#39;t on screen. Same trap the referrer bar hit.
   */
  const decodeEntities = (value) => {
    if (typeof value !== 'string' || !value.includes('&')) return value;
    const box = document.createElement('textarea');
    box.innerHTML = value;
    return box.value;
  };

  /** One Friendly Captcha script for the page however many forms want it. */
  let captchaScript = null;
  const loadCaptcha = () => {
    if (captchaScript) return captchaScript;
    captchaScript = new Promise((resolve) => {
      const s = document.createElement('script');
      s.type = 'module';
      s.src = FRIENDLY_CAPTCHA_SRC;
      s.async = true;
      s.onload = () => resolve(true);
      // Named rather than swallowed: with bot protection on, no widget means
      // every submission is refused by the server, and the form would look
      // perfectly fine while doing nothing.
      s.onerror = () => {
        console.warn('[pruvit] Friendly Captcha did not load from ' + FRIENDLY_CAPTCHA_SRC);
        resolve(false);
      };
      document.head.appendChild(s);
    });
    return captchaScript;
  };

  const setup = async (root) => {
    const sdk = await window.pruvitMVQore;
    if (!sdk) return; // no SDK, no lead capture — leave the markup inert

    const status = await sdk.getStatus();
    if (!status.installed || !status.configured) return;

    const el = {
      ref: root.querySelector('[data-pruvit-ref]'),
      form: root.querySelector('[data-pruvit-lead-form]'),
      // By name, not a data attribute: this field has never carried one, and
      // the name is the contract anyway — attachLeadForm reads `email` too.
      email: root.querySelector('input[name="email"]'),
      phone: root.querySelector('[data-pruvit-lead-phone]'),
      smsBlock: root.querySelector('[data-pruvit-lead-sms-block]'),
      sms: root.querySelector('[data-pruvit-lead-sms-consent]'),
      emailConsent: root.querySelector('[data-pruvit-lead-email-consent]'),
      captcha: root.querySelector('[data-pruvit-lead-captcha]'),
      err: root.querySelector('[data-pruvit-lead-err]'),
      submit: root.querySelector('[data-pruvit-lead-submit]'),
      done: root.querySelector('[data-pruvit-lead-done]'),
      doneBody: root.querySelector('[data-pruvit-lead-done-body]'),
      doneLogin: root.querySelector('[data-pruvit-lead-done-login]'),
    };

    // ---- messages and the referrer step ------------------------------------

    const showErr = (node, message) => {
      node.textContent = decodeEntities(message);
      node.hidden = false;
    };
    const clearErr = (node) => {
      node.textContent = '';
      node.hidden = true;
    };

    // The step is shared with the Pro application; null when this form does
    // not draw it (signed in, on a form with an account step). Mounted before
    // anything else so the submit gate below can ask it.
    const step = window.pruvitReferrerStep ? await window.pruvitReferrerStep.mount(root, sdk) : null;

    // ---- consents ----------------------------------------------------------

    /**
     * What to say about a field the browser has judged invalid.
     *
     * Its own validationMessage is accurate and localised by the browser, but
     * it is the browser's voice in the middle of the theme's, and on a
     * translated storefront it is in the browser's language rather than the
     * page's. So the two cases this form can actually produce get the theme's
     * own words, and anything else falls back to the browser's rather than to
     * a generic line that would say less than it knows.
     */
    const messageForField = (field) => {
      if (field.validity.valueMissing) {
        // A required checkbox is not an empty field, and "please fill this in"
        // reads as nonsense against one. Only two things on this form are
        // required — the email and, on the registration variant, the terms —
        // so both get said properly rather than falling to a generic line.
        if (field.type === 'checkbox') {
          return say('errTerms', 'Please accept the terms to create your account.');
        }
        return field.name === 'email'
          ? say('errEmail', 'Please enter your email address.')
          : say('errRequired', 'Please fill in this field.');
      }
      if (field.validity.typeMismatch && field.type === 'email') {
        return say('errEmailInvalid', 'That doesn\u2019t look like an email address — check for a missing \u2018@\u2019.');
      }
      return field.validationMessage || say('errGeneric', 'Something went wrong. Please try again.');
    };

    // Told before they press the button, which is what was asked for. On blur
    // rather than on every keystroke: flagging an address as malformed while
    // someone is still halfway through typing it is correct and obnoxious.
    // Only once they have entered something — an empty field they have merely
    // passed through is not yet a mistake.
    if (el.email) {
      el.email.addEventListener('blur', () => {
        const value = (el.email.value || '').trim();
        if (value && !el.email.checkValidity()) showErr(el.err, messageForField(el.email));
      });
      // And cleared the moment it is right again, so the message never
      // outlives the problem.
      el.email.addEventListener('input', () => {
        if (el.email.checkValidity()) clearErr(el.err);
      });
    }

    const syncSms = () => {
      const has = Boolean((el.phone.value || '').trim());
      if (!el.sms || !el.smsBlock) return;
      el.sms.disabled = !has;
      el.smsBlock.classList.toggle('is-off', !has);
      if (!has) el.sms.checked = false;
    };
    el.phone.addEventListener('input', syncSms);
    syncSms();

    // ---- captcha -----------------------------------------------------------

    // Not on load. Craig, 2026-09-21: every page view and every modal open was
    // spending a Friendly Captcha verification on someone who might only be
    // browsing. The widget waits for the first keystroke in the email field
    // instead — every form here has one, nobody types an address by accident,
    // and it is the earliest point at which someone is plainly filling this in.
    //
    // Still data-start="auto" once it mounts. `focus` was measured on staging
    // sitting at .UNSTARTED ten seconds after a field was focused, rendering
    // "Click to start verification" — a click nobody would know to give. Auto
    // solves on mount, so the wait moves from page load to first keystroke
    // rather than to submit.
    //
    // The head start is real: the SDK rejects anything submitted within five
    // seconds of render, and after typing an email there are still a name, a
    // number and two consents to go. If it is somehow still solving, the
    // submit path already reports captchaPending rather than posting.
    const bot = await sdk.getBotProtection();
    if (bot.enabled && bot.siteKey) {
      let mounting = null;

      const mountCaptcha = () => {
        if (mounting) return mounting;
        el.captcha.hidden = false;
        el.captcha.classList.add('frc-captcha');
        el.captcha.setAttribute('data-sitekey', bot.siteKey);
        el.captcha.setAttribute('data-start', 'auto');
        mounting = loadCaptcha().then((ok) => {
          // v2 auto-attaches to .frc-captcha elements that exist when the
          // script runs. This one is mounted on demand, so for the second and
          // later forms on a page the script is already loaded and nothing
          // would happen. attach() rescans; calling it when auto-attach
          // already ran is a no-op. Verified on an element added afterwards.
          if (ok && window.frcaptcha && typeof window.frcaptcha.attach === 'function') {
            try {
              window.frcaptcha.attach();
            } catch (e) {
              console.warn('[pruvit] Friendly Captcha attach failed:', e);
            }
          }
          if (!ok) {
            showErr(el.err, say('captchaFailed', 'Could not load the security check. Please reload the page.'));
            el.submit.disabled = true;
          }
          return ok;
        });
        return mounting;
      };

      // once: the first keystroke is the signal; the rest are typing.
      if (el.email) el.email.addEventListener('input', mountCaptcha, { once: true });

      // And a floor under it. Someone who autofills the whole form may never
      // fire an input event on the email field, and someone who reaches the
      // button having skipped it should still be verified rather than blocked
      // — so submitting mounts it too, and the pending path handles the wait.
      //
      // Capture, and this is not optional: the consent guard below is
      // registered with `true` and calls stopImmediatePropagation when the
      // token is missing, which is exactly the state a form in this branch is
      // in on its first submit. A bubble-phase listener here would be killed
      // by that guard, the widget would never mount, and the form would sit
      // on "finishing the security check" forever. Capture listeners run
      // first, and this one is registered before the guard.
      el.form.addEventListener('submit', mountCaptcha, true);
    }

    /**
     * Throw the used answer away after a rejected submission.
     *
     * Prüvit's dev team, 2026-09-24, testing Create Account: "After a failed
     * attempt captcha should be reseted so that next request doesn't send the
     * same captcha." They are right, and it is worse than a stale value — a
     * Friendly Captcha solution is single use. Once the server has presented
     * one to siteverify it is spent, so a second submit carrying the same
     * token is refused for a reason that has nothing to do with whatever went
     * wrong the first time. The visitor fixes the real problem, submits again,
     * and is told no again.
     *
     * Reset on EVERY error, not only the captcha-shaped ones. Whether the
     * server spends the token before or after it validates the email is its
     * business, not something the theme should model; a fresh answer costs a
     * second of background work and is never the wrong thing to hold.
     *
     * Verified against the live widget rather than the docs, 2026-09-24:
     * getAllWidgets() finds it, getElement() matches our host, and reset()
     * moves it completed -> activating -> completed with a DIFFERENT token.
     * data-start="auto" is what makes the re-solve automatic; without it this
     * would leave the widget waiting for a click nobody knows to give.
     */
    const resetCaptcha = () => {
      if (!bot.enabled) return;
      try {
        const api = window.frcaptcha;
        if (!api || typeof api.getAllWidgets !== 'function') return;
        const widget = api
          .getAllWidgets()
          .find((w) => w.getElement && (w.getElement() === el.captcha || el.captcha.contains(w.getElement())));
        if (widget && typeof widget.reset === 'function') widget.reset();
      } catch (e) {
        // Never at the expense of the message. A visitor who cannot be told
        // what went wrong is worse off than one carrying a stale token.
        console.warn('[pruvit] could not reset the captcha:', e);
      }
    };

    // ---- submit ------------------------------------------------------------

    const tags = (root.dataset.tags || '')
      .split(',')
      .map((t) => t.trim())
      .filter(Boolean);

    /**
     * lead_source is the address the form was submitted from, scheme and all.
     *
     * Craig, 2026-09-23: MV Qore reads it to decide whether to notify the
     * referrer in the app, so it has to be a real URL. It used to be a literal
     * per call site — 'blueprint', 'partner-preview', 'flavour-quiz',
     * 'create-account' — which told a human which form it was and told the app
     * nothing it could act on. The forms are still told apart, by their tags.
     *
     * Origin and path only. The query string is dropped on purpose: a
     * ?ref= lands on most of these pages and a cache-buster on some, so
     * keeping it would make almost every submission a unique string and
     * anything matching on this value would have to normalise it first. The
     * market prefix is part of the path and stays — /de-de/pages/k1-partner
     * genuinely is a different page from the /en-au/ one.
     *
     * Read at submit time rather than at attach, so a form in a drawer that
     * the shopper opened on one page and submitted on another reports where
     * they actually were.
     */
    const pageUrl = () => window.location.origin + window.location.pathname;

    // Registration and lead capture are the same form to fill in and two
    // different things to submit. One endpoint creates an MV Qore account and
    // can sign the customer in; the other records a lead. The fields, the
    // referrer step, the captcha and the consents are identical, which is why
    // this file serves both rather than existing twice.
    const isRegister = (root.dataset.mode || 'lead') === 'register';

    const showDone = () => {
      el.form.hidden = true;
      if (el.ref) el.ref.hidden = true;
      el.done.hidden = false;
      el.done.focus();
      // Announced so anything that solicits a lead can stop soliciting. The
      // Blueprint's auto-open listens for this and never fires again on a
      // browser that has converted — nagging someone for an email they have
      // already given is the worst version of this pattern.
      document.dispatchEvent(
        new CustomEvent('pruvit:lead:done', { detail: { mode: root.dataset.mode || 'lead', source: root } })
      );
    };

    const onError = (error) => {
      const code = error && error.code;
      // Before the message, so the widget is already re-solving while they
      // read it. By the time anyone has understood the error and corrected a
      // field there is a fresh answer waiting.
      resetCaptcha();
      // The SDK found no referrer the gate believed was there -- the stored
      // one was cleared in another tab, say. The fix is on the step, so that
      // is where they are sent; the line below still names it.
      if (code === 'REFERRER_REQUIRED' && step) step.require();
      showErr(
        el.err,
        {
          MISSING_EMAIL: say('errEmail', 'Please enter your email address.'),
          EMAIL_IN_USE: say('errInUse', "You're already signed up with that address."),
          LEAD_TOO_FAST: say('errFast', 'Please take a moment before submitting.'),
          REGISTRATION_TOO_FAST: say('errFast', 'Please take a moment before submitting.'),
          REFERRER_REQUIRED: say('errReferrer', 'Please enter a referrer code first.'),
          LEAD_REJECTED: say('errRejected', 'We could not verify that submission. Please try again.'),
          REGISTRATION_REJECTED: say('errRejected', 'We could not verify that submission. Please try again.'),
          // Registration only. A number already on another account is a
          // different problem from an email already on one, and telling
          // someone "that email is taken" when it is the phone sends them
          // round a loop they cannot get out of.
          PHONE_IN_USE: say('errPhoneInUse', 'That phone number is already on an account.'),
        }[code] || say('errGeneric', 'Something went wrong. Please try again.')
      );
    };

    if (isRegister) {
      sdk.attachRegistrationForm(el.form, {
        source: pageUrl(),
        // No tags. The server applies the merchant's Create Account tag and
        // the SDK warns if any are passed — custom tags are a lead-form thing.
        //
        // True, and it was false until 2026-10-05, when QA found an account
        // created with no parentRefcode. Craig: with Referrer Bypass off, a
        // form that creates a customer must not submit without a referrer. The
        // gate below says so in the theme's words first; this is the SDK
        // refusing as well, should anything get past it. "I don't have one",
        // where the merchant offers it, stores their default code as the
        // active referrer, so it passes here too.
        requireReferrer: true,
        autoLogin: true,
        returnTo: root.dataset.returnTo || undefined,
        onSuccess: (result) => {
          showDone();
          // With auto-login working, this page is already navigating and
          // nothing below matters. When it is not — the store's identity
          // provider is off, the ticket expired, storage is blocked — the
          // account still exists and the customer is simply not signed in.
          // Saying so, with a way in, beats a success message that quietly
          // is not the whole truth.
          const auto = result && result.autoLogin;
          if (auto && auto.started) return;
          if (el.doneBody) {
            el.doneBody.textContent = say(
              'doneNoLogin',
              'Your account is ready. Sign in to get started.'
            );
          }
          if (el.doneLogin) el.doneLogin.hidden = false;
        },
        onError,
      });
    } else {
      sdk.attachLeadForm(el.form, {
        tags: tags.length ? tags : null,
        source: pageUrl(),
        // The step above is the referrer question, and it is asked before the
        // form rather than by it. Leaving this true would have the SDK reject a
        // submission on a condition the theme already checked and reported in
        // its own words. Note this governs the *lead*, not the purchase: where
        // Referrer Bypass is off, checkout enforces the referrer regardless of
        // what happens here.
        requireReferrer: false,
        onSuccess: showDone,
        onError,
      });
    }

    // Consent is the theme's gate, not the SDK's: it posts whatever it is
    // given. Checked in the capture phase so it runs before the SDK's own
    // submit handler and can stop it.
    el.form.addEventListener(
      'submit',
      (event) => {
        // Nothing to copy any more. The v2 widget writes frc-captcha-response
        // itself, which is the name MVQore.attachLeadForm reads, so the form
        // carries one field with that name and the widget owns it. Read here
        // only to decide whether it has an answer yet.
        const solved = el.captcha.querySelector('input[name="frc-captcha-response"]');
        const value = solved ? solved.value || '' : '';

        // REFERRER FIRST, on a form that creates an account. Craig,
        // 2026-10-05: with Referrer Bypass off, registration must not go
        // through without a referrer, and the button stays live and explains
        // itself rather than greying out — a disabled button cannot say why.
        // First because the step sits above everything else on the form, so
        // this is the order the person meets the questions in. Leads are not
        // gated: they create no account, and checkout enforces the referrer.
        if (isRegister && step && !step.has()) {
          event.preventDefault();
          event.stopImmediatePropagation();
          clearErr(el.err);
          step.require();
          return;
        }

        // FIELDS FIRST, and this one was simply missing.
        //
        // The form carries `novalidate` on purpose — the browser's own bubbles
        // cannot be styled and arrive one field at a time — which switches off
        // every native check, not just the ugly presentation of them. The
        // theme then implemented the consent gate and the captcha gate and
        // never the field gate, so an email with no @ in it went to the server
        // and came back as the generic "Something went wrong", which is both
        // wrong and unhelpful: it was not the server's fault and there is
        // nothing generic about it. QA found it, 2026-09-23.
        //
        // `novalidate` suppresses the UI, not the validity API, so the answers
        // are still there to read — checkValidity() and the ValidityState
        // flags. This reports them in the form's own error line, which is
        // where every other message here appears.
        //
        // Before consent, because a malformed email is a mistake in something
        // they have already typed, and it is the field their attention is
        // nearest to.
        const invalid = [...el.form.elements].find(
          (field) => typeof field.checkValidity === 'function' && !field.checkValidity()
        );
        if (invalid) {
          event.preventDefault();
          event.stopImmediatePropagation();
          showErr(el.err, messageForField(invalid));
          invalid.focus();
          return;
        }

        // Consent before the captcha, and the order is the point.
        //
        // These are two different kinds of problem. A missing consent is
        // something the person can fix in one tap; a captcha still solving is
        // something they can only wait out. Reporting the wait first tells
        // someone to sit still when what they actually need is to tick a box
        // — and once the box is ticked they get the wait anyway, so they have
        // been told two things in the least useful order.
        //
        // This was the wrong way round until 2026-09-21 and it did not show
        // while the captcha started on page load, because by the time anyone
        // reached the button it had long since solved. Moving the mount to the
        // first email keystroke closed that gap to seconds and the ordering
        // started to matter. Measured: fill the email, submit, and the form
        // said "finishing the security check" with the consent box still
        // empty.
        // Absent when the signed-in customer has already opted in: the box is
        // not rendered and a hidden field carries the value instead. No
        // control to check means nothing to gate on, and the permission is
        // already held.
        if (el.emailConsent && !el.emailConsent.checked) {
          event.preventDefault();
          event.stopImmediatePropagation();
          showErr(el.err, say('errConsent', 'Please agree to receive emails so we can send this to you.'));
          el.emailConsent.focus();
          return;
        }

        // Friendly Captcha parks a sentinel in that field until it has an
        // answer. v2's are .UNCONNECTED, .UNACTIVATED, .UNFINISHED, .ERROR,
        // .EXPIRED — different words from v1's, same shape: all leading with a
        // dot, none of them a token, so the test did not need changing. Sending one is a refusal
        // from the server phrased as though the person did something wrong, so
        // it is caught here and named as the wait it actually is.
        if (bot.enabled && (!value || value.startsWith('.'))) {
          event.preventDefault();
          event.stopImmediatePropagation();
          showErr(el.err, say('captchaPending', 'Just a moment — finishing the security check.'));
          return;
        }

        clearErr(el.err);
      },
      true
    );
  };

  for (const root of roots) {
    setup(root).catch((error) => console.warn('[pruvit] lead form could not start:', error));
  }
})();
